import { Fragment, isValidElement } from 'react'
import HelpOutlineOutlined from '@mui/icons-material/HelpOutlineOutlined'
import Typography from '@mui/material/Typography'
import { styled } from '@mui/material/styles'
import Tooltip from './Tooltip'

const Container = styled('dl')(({ theme }) => ({
    width: '100%',
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    gridTemplateRows: 'repeat(auto-fill, auto)',
    gridRowGap: theme.spacing(1),
    gridColumnGap: theme.spacing(3),
    '& dd': {
        margin: 0,
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
    },
}))

const Label = styled('div')(({ theme }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
}))

export interface DataSetProps {
    values: {
        [key: string]: string | number | undefined | React.ReactNode
    }
    tooltips?: {
        [key: string]: React.ReactNode
    }
}

/**
 * Common data set display
 * @param values - map of key=title, value=value to show
 * @param tooltips - optional map of key -> tooltip content; rendered as a help icon next to the label
 */
const DataSet = ({ values, tooltips }: DataSetProps) => {
    return (
        <Container>
            {Object.entries(values).map(([key, value]) => {
                const tooltip = tooltips?.[key]
                return (
                    <Fragment key={key}>
                        <dt>
                            <Label>
                                <Typography sx={{ fontWeight: 'bold' }}>{key}</Typography>
                                {tooltip && (
                                    <Tooltip title={tooltip}>
                                        <HelpOutlineOutlined fontSize="small" color="action" />
                                    </Tooltip>
                                )}
                            </Label>
                        </dt>
                        <dd data-value-for={key}>
                            {isValidElement(value) ? value : <Typography component="span">{value}</Typography>}
                        </dd>
                    </Fragment>
                )
            })}
        </Container>
    )
}

export default DataSet
